// What a rep actually confirmed, and whether it still holds.
//
// ── Why this exists ───────────────────────────────────────────────────────
// Confirmation used to store a timestamp and nothing else, so the green badge
// vouched for whatever the numbers happened to be at read time rather than what
// the rep saw when they clicked. Corrections land after confirmation as a matter
// of routine — an invoice reattributed from one rep to another in the Sales Log,
// or an adjustment entered with a memo — and the badge stayed green through all
// of it. A rep confirmed, an invoice he had wrongly taken credit for was moved
// away, and his confirmation still read as agreement with a number that no
// longer existed.
//
// So a confirmation now records the STATE it was made against, and staleness is
// derived by rebuilding that state and comparing.
//
// ── Computed, never flagged ───────────────────────────────────────────────
// Nothing marks a confirmation stale at edit time. There is no hook on the
// Sales Log, no trigger, no flag column to keep in sync. Staleness is a pure
// function of (snapshot, current state) evaluated on render.
//
// That is the whole design. An edit-time flag has to be fired from every path
// that can change a rep's month — Sales Log edits, deletes, reattribution,
// adjustments, the parser, a promote from Needs Action, a manual SQL fix — and
// the one path someone forgets is silent, which is exactly the failure being
// fixed. Recomputing cannot be forgotten.
//
// It also makes reattribution work in both directions for free: moving an
// invoice from RTR to AGL removes an entry from RTR's rebuilt state and adds one
// to AGL's, so both go stale without either edit knowing about confirmations.

/**
 * The rep's confirmable state for a month.
 *
 * `entries` MUST already be filtered by the caller with the same rule the page
 * displays — App.js `salesEntries`: tier not loaner/nocomm, amount ≠ 0,
 * actionType not duplicate/credit_memo. Passing the filter in rather than
 * duplicating it here is deliberate: a snapshot that disagreed with the screen
 * would make a rep stale for something they were never shown.
 *
 * @param {Array}    entries   already filtered to this rep + month
 * @param {Function} commissionFor  (entry) => number. App.js passes getComm.
 * @param {number}   adjustmentsCommission  commission effect of adjustments and
 *   resolved reversals. Folded into total_commission so an adjustment-only
 *   change still stales the confirmation — correction type (b) — without
 *   inventing a second array the spec does not describe.
 */
export function buildCommissionSnapshot({ rep, month, entries, commissionFor, adjustmentsCommission = 0 }) {
  const rows = (entries || []).map(e => ({
    entry_id: e.id,
    invoice: String(e.invoice ?? ''),
    amount: round2(e.amount),
    tier: e.tier ?? null,
    commission: round2(commissionFor(e)),
  })).sort((a, b) => String(a.entry_id).localeCompare(String(b.entry_id), undefined, { numeric: true }))

  const entriesCommission = round2(rows.reduce((s, r) => s + r.commission, 0))

  return {
    rep,
    month,
    entries: rows,
    entry_count: rows.length,
    // The number the rep was shown as their total — entries plus adjustments.
    total_commission: round2(entriesCommission + (Number(adjustmentsCommission) || 0)),
  }
}

function round2(n) {
  const v = Number(n)
  return Number.isFinite(v) ? Math.round(v * 100) / 100 : 0
}

/**
 * What changed between the confirmed snapshot and the current state.
 *
 * Keyed on entry_id, not invoice: a reattributed invoice keeps its id and moves
 * between reps, and two reps can legitimately hold the same invoice number
 * across a split. Comparing on invoice would show a reattribution as "same
 * invoice, different rep" in neither rep's diff.
 */
export function diffSnapshots(confirmed, current) {
  const before = new Map((confirmed?.entries || []).map(r => [String(r.entry_id), r]))
  const after = new Map((current?.entries || []).map(r => [String(r.entry_id), r]))

  const added = []
  const removed = []
  const modified = []

  for (const [id, row] of after) if (!before.has(id)) added.push(row)
  for (const [id, row] of before) if (!after.has(id)) removed.push(row)
  for (const [id, now] of after) {
    const was = before.get(id)
    if (!was) continue
    const fields = []
    if (round2(was.amount) !== round2(now.amount)) fields.push('amount')
    if ((was.tier ?? null) !== (now.tier ?? null)) fields.push('tier')
    if (round2(was.commission) !== round2(now.commission)) fields.push('commission')
    if (fields.length) modified.push({ entry_id: id, invoice: now.invoice, fields, was, now })
  }

  const wasTotal = round2(confirmed?.total_commission)
  const nowTotal = round2(current?.total_commission)
  // Reported separately so an adjustment-only change is still a visible reason
  // to re-review, rather than a stale badge with an empty diff.
  const totalChanged = wasTotal !== nowTotal ? { from: wasTotal, to: nowTotal } : null

  return {
    added, removed, modified, totalChanged,
    count: added.length + removed.length + modified.length + (totalChanged ? 1 : 0),
  }
}

export const CONFIRM_STATE = { NONE: 'none', CONFIRMED: 'confirmed', STALE: 'stale', LEGACY: 'legacy' }

/**
 * Render-time status for one rep+month.
 *
 * `legacy` exists because confirmations predating this table stored only a
 * timestamp. Those are real confirmations and must not read as "never
 * confirmed", but they carry no snapshot so they can never be diffed — they are
 * shown as confirmed, without a staleness claim either way.
 */
export function confirmationStatus({ snapshot, current, legacyConfirmedAt = null }) {
  if (!snapshot) {
    return legacyConfirmedAt
      ? { state: CONFIRM_STATE.LEGACY, confirmedAt: legacyConfirmedAt, diff: null }
      : { state: CONFIRM_STATE.NONE, confirmedAt: null, diff: null }
  }
  const diff = diffSnapshots(snapshot.snapshot || snapshot, current)
  return {
    state: diff.count === 0 ? CONFIRM_STATE.CONFIRMED : CONFIRM_STATE.STALE,
    confirmedAt: snapshot.confirmed_at || null,
    diff,
  }
}

/** The newest non-superseded confirmation for a rep+month, or null. */
export function latestConfirmation(rows, rep, month) {
  return (rows || [])
    .filter(r => r.rep === rep && r.month === month && !r.superseded)
    .sort((a, b) => String(b.confirmed_at || '').localeCompare(String(a.confirmed_at || '')))[0] || null
}
